import { toast } from 'react-toastify';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactElement } from 'react';
import { describeApiError } from '../../../utils/apiError';

const ToastContent = (IconComponent: ReactElement, message: string) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    {IconComponent}
    <span>{message}</span>
  </Box>
);

export const notifySuccess = (message: string) => {
  toast.success(ToastContent(<CheckCircleIcon sx={{ color: 'green' }} />, message));
};

export const notifyError = (message: string, options?: Record<string, any>) => {
  toast.error(ToastContent(<CancelIcon sx={{ color: 'red' }} />, message), options);
};

export const notifyInfo = (message: string) => {
  toast.info(ToastContent(<InfoIcon sx={{ color: 'blue' }} />, message));
};

/** Error toast that also offers the user a way back — used when a retry can actually help. */
const RetryableToastContent = (message: string, onRetry: () => void) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <CancelIcon sx={{ color: 'red', mt: '2px' }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
      <span>{message}</span>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        onClick={(e) => {
          e.stopPropagation();
          onRetry();
        }}
        sx={{ textTransform: 'none', fontSize: 12, py: 0.1, px: 1, minWidth: 0 }}
      >
        Retry now
      </Button>
    </Box>
  </Box>
);

/**
 * Reports an API failure in language the user can act on.
 *
 * Replaces the blanket "Failed to load orders" toast: the message now names
 * the actual cause and carries a trace code. `toastId` collapses repeats, so
 * a backend outage during the 15-second dashboard poll produces one visible
 * toast rather than one per tab per cycle.
 *
 * @param error   the rejected axios error
 * @param subject what the user was trying to see, e.g. "orders"
 * @param onRetry optional immediate retry, offered only when retrying can help
 */
export const notifyApiError = (
  error: any,
  subject: string = 'data',
  onRetry?: () => void
) => {
  const described = describeApiError(error, subject);

  // Requests cancelled by navigation or a filter change are not failures.
  if (described.silent) return described;

  if (process.env.NODE_ENV === 'development') {
    console.error(`[api:${subject}] ${described.code}`, error);
  }

  const options = { toastId: described.toastId, autoClose: 6000 };

  if (described.retryable && onRetry) {
    toast.error(RetryableToastContent(described.message, onRetry), options);
  } else {
    notifyError(described.message, options);
  }

  return described;
};
